import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faCircleNotch, faWheelchair, faLongArrowAltLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Direction, DirectionMappings, DirectionRequest, DirectionsModel, NavigationModel, Room } from '../shared/models/directions/directions';
import { DirectionLocationType } from '../shared/models/directions/direction-location-type';
import { DirectionsService } from './services/directions.service';

@Component({
    selector: 'app-directions',
    templateUrl: './directions.component.html',
    styleUrls: ['./directions.component.scss'],
    standalone: false
})
export class DirectionsComponent implements OnInit {
  public faWheelchair = faWheelchair;
  public faLongArrowAltLeft = faLongArrowAltLeft;
  public faExclamationTriangle = faExclamationTriangle;
  public faCircleNotch = faCircleNotch;
  public showSpinner: boolean = true;
  public showSpinnerDetail: boolean = false;
  public startLocation: string = '';
  public endLocation: string = '';
  public startLocationType: DirectionLocationType = 0;
  public endLocationType: string = '';
  public wheelchairAccessible: boolean = false;
  public full: boolean = false;
  public locations: Direction[] = [];
  public api: string = 'https://app-shg-prod.azurewebsites.net';
  public hearingRooms: Direction = this.initializeDirection();
  public representatives: Direction = this.initializeDirection();
  public senators: Direction = this.initializeDirection();
  public houseComm: Direction = this.initializeDirection();
  public senComm: Direction = this.initializeDirection();
  public jointCommSenate: Direction = this.initializeDirection();
  public jointCommHouse: Direction = this.initializeDirection();
  public pointsOI: Direction = this.initializeDirection();
  public currHeader: string = 'Hearing Rooms';
  public currHeaderSelected: boolean = false;
  public room: string | undefined;
  public target: string = '';
  public onSelected: boolean = false;
  public img: string | undefined;
  public initiated = false;
  public showNoDirection: boolean = false;
  public directions: DirectionsModel = {
    startName: '',
    endName: '',
    navigation: [] as NavigationModel[]
  };
  public roomSelected: Room = {
    alts: [],
    code: '',
    icon: '',
    qrTitle: '',
    sortTitle: '',
    subtitle: '',
    target: '',
    targetType: 0,
    title: '',
    warning: '',
    letterId: ''
  };

  constructor(
    protected http: HttpClient,
    protected route: ActivatedRoute,
    protected router: Router,
    protected service: DirectionsService) { }

  ngOnInit() {
    this.initiated = false;
    const url = this.router.url;
    this.room = this.extractRoomFromUrl(url);

    this.service.getAllDirections().subscribe(result => {
      if (result) {
        this.initiated = true;
        this.showSpinner = false;
        this.locations = result;

        const DirectionMappings: DirectionMappings = {
          hearingRooms: result[0],
          senators: result[1],
          representatives: result[2],
          senComm: result[3],
          houseComm: result[4],
          jointCommSenate: result[5],
          jointCommHouse: result[6],
          pointsOI: result[7],
        };
        Object.keys(DirectionMappings).forEach(property => {
          const Direction = DirectionMappings[property];
          if (Direction) {
            (this as any)[property] = Direction;
            if (Direction.items) {
              Direction.items.sort((a, b) => a.sortTitle.localeCompare(b.sortTitle));
            }
          }
        });

        if (this.room && !this.room.includes('direction')) {
          const selectedRoom = this.selectRoom(result, this.room)?.room as Room;
          if (selectedRoom) {
            this.roomSelected = selectedRoom;
            this.currHeader = this.selectRoom(result, this.room)?.type as string;
            this.onShowDirection(selectedRoom, this.room.replace(/%20/g, ' '), true);
          } else {
            this.showSpinnerDetail = false;
            this.currHeader = '';
            this.room = undefined;
          }
        } else {
          this.currHeader = '';
          this.room = undefined;
        }
      }
    }, error => {
      console.error(error);
      this.showSpinner = false; // Handle error by stopping spinner
    });
  }

  extractRoomFromUrl(url: string): string | undefined {
    const startIndex = url.indexOf("directions/") + "directions/".length;
    const endIndex = url.lastIndexOf("/");
    if (startIndex !== -1 && endIndex !== -1) {
      return url.substring(startIndex, endIndex);
    } else {
      return undefined;
    }
  }

  onExpand(event?: any) {
    this.onSelected = false;
    this.room = undefined;
    this.showSpinnerDetail = false;
    if (this.currHeader === event.target.innerText) {
      this.currHeader = '';
    } else {
      this.currHeader = event ? event.target.innerText : this.currHeader;
    }
  }

  onShowDirection(room: Room, hearingRoom?: string | null, showSpinner?: boolean) {
    this.showSpinnerDetail = showSpinner;
    this.showNoDirection = false;
    this.roomSelected = room;
    this.onSelected = true;
    this.room = room.title !== '' ? room.title : hearingRoom as string;
    this.target = room.target !== '' ? room.target : hearingRoom as string;
    this.startLocation = '3343';
    this.endLocation = room.target !== '' ? room.target : hearingRoom as string;
    this.startLocationType = DirectionLocationType.Way;
    this.endLocationType = room.targetType.toString() !== '0' ? room.targetType.toString() : 'Room#HEARING-ROOMS_';
    this.full = true;

    var direction = {
      start: this.startLocation,
      end: this.endLocation,
      wheel: this.wheelchairAccessible,
      st: '3',
      et: this.endLocationType,
      full: this.full,
      ignore: false,
      itemName: null
    } as unknown as Direction;

    var directionRequest = {
      direction: direction
    } as unknown as DirectionRequest;

    this.service.getDirectionDetail(directionRequest).subscribe(result => {
      if (result && result.navigation.length > 0) {
        this.showSpinnerDetail = false;
        result.navigation.forEach(nav => {
          nav.route.forEach(dir => {
            dir.directions = dir.directions.replace(/&amp;/g, '&');
          });
        });
        this.directions = result;
        this.img = this.api + result.navigation[0].imageString;
      } else {
        this.showNoDirection = true;
      }
    }, error => console.error(error));
    return;
  }

  selectRoom(directions: Direction[], roomCode: string): { room: Room; type: string } | undefined {
    const selectedRoom = { room: this.initializeRoom(), type: '' };

    for (const direction of directions) {
      for (const item of direction.items) {
        if (item.code === roomCode || item.target === roomCode) {
          selectedRoom.room = item;
          selectedRoom.type = direction.name;
          return selectedRoom;
        }
      }
    }
    return undefined;
  }

  private initializeRoom(): Room {
    return {
      alts: [],
      code: '',
      icon: '',
      qrTitle: '',
      sortTitle: '',
      subtitle: '',
      target: '',
      targetType: 0,
      title: '',
      warning: '',
      letterId: ''
    };
  }

  onCheckBox() {
    this.wheelchairAccessible = !this.wheelchairAccessible;
    if (this.roomSelected && this.room && !this.room.includes('direction')) {
      this.onShowDirection(this.roomSelected, null, true);
    }
  }

  onChangeRoom(event: any) {
    if (event.target.id === 'firstFloorButtonb' || event.target.id === 'firstFloorButton') {
      return this.img = this.api + this.directions.navigation[0].imageString;
    }
    if (event.target.id === 'secondFloorButtonb' || event.target.id === 'secondFloorButton') {
      return this.img = this.api + this.directions.navigation[1].imageString;
    }
    if (event.target.id === 'thirdFloorButtonb' || event.target.id === 'thirdFloorButton') {
      return this.img = this.api + this.directions.navigation[2].imageString;
    }
    return;
  }

  private initializeDirection(): Direction {
    return {
      items: [],
      jumpLinks: false,
      locationType: 0,
      name: ''
    };
  }
}
